import { Locale } from "./locale";

import z from "zod";
import { TargetingWithoutExclude } from "./graphql/types/targeting";
import { BridgeFunctionName } from "./bridge";
import { RelatedMediaItem } from "./graphql/types/relatedMedia";

export const contentTypeIds = {
  countdownTimer: "countdownTimer",
  migrationData: "migrationData",
  global: "global",
  socialLink: "socialLink",
  category: "category",
  newsEntry: "newsEntry",
  esportNewsEntry: "esportNewsEntry",
  newsEntryArticle: "newsEntryArticle",
  smallNewsEntryArticle: "smallNewsEntryArticle",
  newsEntryVideo: "newsEntryVideo",
  newsEntryExternalLink: "newsEntryExternalLink",
  newsEntryImage: "newsEntryImage",
  smallNewsEntryExternalLink: "smallNewsEntryExternalLink",
  featureBlock: "featureBlock",
  textBlock: "textBlock",
  imageBlock: "imageBlock",
  pollBlock: "pollBlock",
  leaderboardBlock: "leaderboardBlock",
  carouselEvent: "carouselEvent",
  carouselEventEntry: "carouselEventEntry",
  carouselEventPollEntry: "carouselEventPollEntry",
  carouselEventPollTargetsEntry: "carouselEventPollTargetsEntry",
  carouselEventProgressTrackerEntry: "carouselEventProgressTrackerEntry",
  unifiedButton: "unifiedButton",
  gameNotification: "gameNotification",
  videoBlock: "videoBlock",
  communityCreator: "communityCreator",
  communityVideoEntry: "communityVideoEntry",
  communityLivestreamEntry: "communityLivestreamEntry",
  spotlight: "spotlight",
  esportSpotlight: "esportSpotlight",
  localizedSpotlight: "localizedSpotlight",
  hero: "hero",
  localizedHero: "localizedHero",

  targeting: "targeting",

  webEvent: "webEvent",
  localizedWebEvent: "localizedWebEvent",

  esportCarouselEntry: "esportCarouselEntry",
  esportLiveEventEntry: "esportLiveEventEntry",
  esportCarouselEventEntry: "esportCarouselEventEntry",
  esportRewatchMatchEntry: "esportRewatchMatchEntry",

  fullscreenEntry: "fullscreenEntry",
  esportFullscreenEntry: "esportFullscreenEntry",
  fullscreenEntryCarousel: "fullscreenEntryCarousel",

  relatedMedia: "relatedMedia",

  // Deprecated
  esportSocialMediaLinkContainer: "esportSocialMediaLinkContainer",
  socialMedia: "socialMedia",
  blogGlobal: "blogGlobal",
  fullscreenEntryCarouselSlide: "fullscreenEntryCarouselSlide",
  esportSocialMediaLink: "esportSocialMediaLink",
  externalButton: "externalButton",
  linkToArticleButton: "linkToArticleButton",
  buttonBlock: "buttonBlock",
  carouselEventVersusTrackerEntry: "carouselEventVersusTrackerEntry",
  carouselEventTugOfWarTrackerEntry: "carouselEventTugOfWarTrackerEntry",
  creatorBlock: "creatorBlock",
} as const;

export const ContentTypeId = z.nativeEnum(contentTypeIds);
export type ContentTypeId =
  (typeof contentTypeIds)[keyof typeof contentTypeIds];

export type ContentTypeAndField = `${ContentTypeId}:${string}`;
// Content that is not tied to any article etc
export const standAloneContent: ContentTypeId[] = [
  contentTypeIds.category,
  contentTypeIds.blogGlobal,
  contentTypeIds.global,

  contentTypeIds.socialLink,
  contentTypeIds.socialMedia,

  contentTypeIds.esportSocialMediaLink,
  contentTypeIds.esportSocialMediaLinkContainer,
  contentTypeIds.migrationData,
  contentTypeIds.communityCreator,
  contentTypeIds.communityVideoEntry,
  contentTypeIds.communityLivestreamEntry,
  contentTypeIds.gameNotification,
];

export const articleRelatedContent: ContentTypeId[] = [
  Object.values(contentTypeIds).filter((id) => !standAloneContent.includes(id)),
].flat();

export const topLevelContentIds = [
  contentTypeIds.newsEntry,
  contentTypeIds.carouselEvent,
  contentTypeIds.esportNewsEntry,
  contentTypeIds.esportCarouselEntry,
  contentTypeIds.fullscreenEntry,
  contentTypeIds.esportFullscreenEntry,
  contentTypeIds.spotlight,
  contentTypeIds.esportSpotlight,
  contentTypeIds.hero,
  contentTypeIds.webEvent,
] as const;

export const immediateChildContentTypes: ContentTypeId[] = [
  contentTypeIds.localizedWebEvent,
  contentTypeIds.carouselEventEntry,
  contentTypeIds.carouselEventPollEntry,
  contentTypeIds.carouselEventPollTargetsEntry,
  contentTypeIds.carouselEventProgressTrackerEntry,

  contentTypeIds.newsEntryArticle,
  contentTypeIds.smallNewsEntryArticle,
  contentTypeIds.newsEntryVideo,
  contentTypeIds.newsEntryExternalLink,
  contentTypeIds.smallNewsEntryExternalLink,

  contentTypeIds.esportLiveEventEntry,
  contentTypeIds.esportCarouselEventEntry,
  contentTypeIds.esportRewatchMatchEntry,
  contentTypeIds.fullscreenEntryCarousel,
];

export const deprecatedContentTypes: ContentTypeId[] = [
  contentTypeIds.blogGlobal,
  contentTypeIds.buttonBlock,
  contentTypeIds.carouselEventTugOfWarTrackerEntry,
  contentTypeIds.carouselEventVersusTrackerEntry,
  contentTypeIds.creatorBlock,
  contentTypeIds.esportSocialMediaLink,
  contentTypeIds.esportSocialMediaLinkContainer,
  contentTypeIds.externalButton,
  contentTypeIds.fullscreenEntryCarouselSlide,
  contentTypeIds.linkToArticleButton,
  contentTypeIds.socialMedia,
];

export const notReleasedContentTypes: ContentTypeId[] = [
  contentTypeIds.hero,
  contentTypeIds.localizedHero,
  contentTypeIds.spotlight,
  contentTypeIds.localizedSpotlight,
  contentTypeIds.esportSpotlight,
];

export type TopLevelContentTypeId = (typeof topLevelContentIds)[number];

export const TaggableEntryType = z.enum(topLevelContentIds);

export type TaggableEntryType = z.infer<typeof TaggableEntryType>;

export const GameName = z.enum([
  "boombeach",
  "brawlstars",
  "clashofclans",
  "clashroyale",
  "hayday",
  "squadbusters",
  "rogue",
]);

export type GameName = z.infer<typeof GameName>;

export const humanReadableGameName: Record<GameName, string> = {
  brawlstars: "Brawl Stars",
  clashofclans: "Clash of Clans",
  clashroyale: "Clash Royale",
  hayday: "Hay Day",
  rogue: "mo.co",
  boombeach: "Boom Beach",
  squadbusters: "Squad Busters",
};

export const gamesWithFullscreen: GameName[] = ["brawlstars"];
export const gamesWithStaticBuild: GameName[] = ["rogue", "clashofclans"];

// Adding stuff to this requires some manual setup in Phrase, creating the correct domains and setting up the correct webhooks
export const gamesWithPhrase: GameName[] = [
  "brawlstars",
  "clashofclans",
  "clashroyale",
  "hayday",
  "squadbusters",
  "rogue",
];

export const gamesWithSnowplow: GameName[] = [
  "boombeach",
  "brawlstars",
  "clashofclans",
  "clashroyale",
  "hayday",
  "rogue",
  "squadbusters",
];

export const NotificationType = z.enum(["standard", "medium", "high"]);
export type NotificationType = z.infer<typeof NotificationType>;
export const notificationTypes: NotificationType[] = ["standard"];

export const ButtonColorVariant = z.enum([
  "yellow",
  "gray",
  "blue",
  "red",
  "red-play",
  "green",
  "gold",
  "pink",
  "orange",
  "twitch",
  "light-blue",
  "dark-blue",
  "purple",
  "cyan",
]);

export type ButtonColorVariant = z.infer<typeof ButtonColorVariant>;

export type Category = {
  id: string;
  name: { [key in Locale]?: string };
  color?: string;
  secondaryColor?: string;
};

export type ImageProps = {
  title: string;
  path: string;
  width: number;
  height: number | undefined;
};

export type DefaultImage = {
  default: ImageProps;
};

export type Image = {
  small: ImageProps;
  smallRetina: ImageProps;
  medium: ImageProps;
  mediumRetina: ImageProps;
  large: ImageProps;
  largeRetina: ImageProps;
};

export type NewsEntryDetails = {
  id?: string;
  type: string;
  title: string | null;
  body?: string;
  image?: Image;

  label?: string | null;
  article?: { id: string; section: Section };
  url?: string | { id: string; section: Section } | null;
  urlType?: string;
  variant?: string | null;
  accordion?: "yes" | "no";
  fullWidth?: "yes" | "no";
  thumbnail?: Image;
  embed?: {
    id: string;
    provider: VideoProvider | "";
    url: string | null;
  };
  poll?: {
    pollId: string;
    options: { id: string; title: string; image: Image }[];
  };
  position?: "left" | "right";
  order?: string;
  functionName?: BridgeFunctionName;
  functionArgs?: string[];
}[];

export type Metadata = {
  id: string;
  version?: number;
  publishedVersion?: number;
  publishedAt?: string;
};

export type Section =
  | "newsEntries"
  | "esportNewsEntries"
  | "carouselEventPollEntry";

export type NewsEntryCommon = {
  id: number | string;
  index: number;
  metadata: Metadata;
  postDate: number;
  targeting: TargetingWithoutExclude | null;
  relatedMedia: RelatedMediaItem[] | null;
  lang: string;
  section: Section;
  title: string;
  categories: {
    title: string;
    color: string;
    secondaryColor: string | null;
  }[];
  newUntil: number | null;
};

type NewsEntryType = "newsEntry" | "esportNewsEntry";

export type ContentJsonNewsEntry = NewsEntryCommon & {
  type: NewsEntryType;
  listingSize: "small" | "large";
  showInNewsFeed?: ShowInNewsFeedKind;
  thumbnail?: Image;
  thumbnailAlt?: Image;
  heroImage?: Image;
  heroProgressTracker?: ArticleHeroProgressTracker;
  details: NewsEntryDetails;
};

export type FullscreenContentCarouselSlide = {
  id: string;
  backgroundMedia?: Image;
  body?: string;
};

export type FullscreenContentCarousel = {
  id: string;
  targeting: TargetingWithoutExclude | null;
  relatedMedia: RelatedMediaItem[] | null;
  type: "fullscreenCarousel" | "esportFullscreenCarousel";
  readMoreButton?: Button;
  slides: FullscreenContentCarouselSlide[];
};

type VideoNewsEntryType = "videoNewsEntry";

export type VideoProvider = "youtube" | "twitch" | "tiktok" | "snapchat";

export type ContentJsonVideoEntry = NewsEntryCommon & {
  type: VideoNewsEntryType;

  title: string;
  creator?: {
    name: string;
    icon: string;
  };

  embed: {
    id: string;
    livepid?: string;
    provider: VideoProvider | "";
    url: string;
  };

  // TODO: These are constants for now, not needed atm
  embedVideo: false;
  listingSize: "large";

  thumbnail?: Image;
};

export type ContentJsonEsportRewatchEntry = {
  id: number | string;
  postDate: number;
  lang: string;
  section: Section;
  type: "esportRewatchMatchEntry";

  playerOneName: string;
  playerOneScore?: number;
  playerOneImage?: Image;

  playerTwoName: string;
  playerTwoScore?: number;
  playerTwoImage?: Image;

  matchThumbnail?: Image;
  matchUrl?: string;
  provider?: VideoProvider;
};

export type ContentJsonExternalLinkEntry = NewsEntryCommon & {
  type: "externalNewsEntry" | "esportExternalNewsEntry";

  title: string;
  creator?: {
    name: string;
    icon: string;
  };

  url: string;

  listingSize: "small" | "large";

  thumbnail?: Image;
  thumbnailAlt?: Image;
};

export type ContentJsonImageNewsEntry = NewsEntryCommon & {
  type: "imageNewsEntry";

  thumbnail?: Image;
};

export type UrlButton = {
  title: null;
  type: "url";
  urlType: "url";
  id: string;
  order: string;
  variant: string;
  url: string;
  label: string;
};

export type ArticleButton = {
  title: null;
  type: "article";
  urlType: "article";
  id: string;
  order: string;
  variant: string;
  label: string;

  url: {
    id: string;
    section: Section;
  };

  article: {
    id: string;
    section: Section;
  };
};

export type BridgeFunctionButton = {
  title: null;
  type: "bridgeFunction";
  urlType: "bridgeFunction";
  id: string;
  order: string;
  variant: string;
  label: string;
  functionName: BridgeFunctionName;
  functionArgs: string[];
};

export type Button = UrlButton | ArticleButton | BridgeFunctionButton;

export type CarouselCommonContent = {
  id: string;
  metadata: Metadata;
  postDate: number;
  sortDate: number;
  lang: Locale;
  targeting: TargetingWithoutExclude | null;
  relatedMedia: RelatedMediaItem[] | null;
  title: string;
  ctas: Button[];
};

export type CarouselEventContent = CarouselCommonContent & {
  type: "eventEntry";
  displayTimer: boolean;
  timerLabel?: string;
  timerTarget?: number;
  timerKind?: CountdownTimerKind;
  hideTitle: boolean;
  background?: Image;
};

export type CarouselPollOption = {
  id: string;
  title: string;
  image: Image;
};

export type CarouselPollTargetsOption = {
  id: string;
  title: string;
  image: Image;
};

export type CarouselPollContent = CarouselCommonContent & {
  type: "pollEntry";
  hideTitle: boolean;
  background?: Image;
  pollTitle: string;
  options: CarouselPollOption[];
};

export type PollTargets = {
  id: string;
  value: number | undefined;
  name: string | undefined;
  imageURL: string | undefined;
  maxBan?: number | undefined;
};

export type CarouselPollTargetsContent = CarouselCommonContent & {
  type: "pollEntry";
  hideTitle: boolean;
  background?: Image;
  pollTitle: string;
  options: CarouselPollOption[];
  pollTargets: PollTargets[];
  pollDescription: string;
};

export type ProgressMilestone = {
  progress: number;
  label?: string;
  voucherId?: string | null;
  eventId?: string | null;
  unlocked: boolean;
  image?: Image;
};

export type ProgressTracker = {
  progress: number;
  rawProgressInMillions?: number; // TODO: Remove after echo-vault
  color?: string;
  image?: Image;
  direction: "vertical" | "horizontal";
};

export type CarouselProgressTrackerContent = CarouselCommonContent & {
  type: "progressTrackerEntry";
  tracker: ProgressTracker;
  milestones: ProgressMilestone[];
  background?: Image;
};

export type CarouselContent =
  | CarouselEventContent
  | CarouselPollContent
  | CarouselPollTargetsContent
  | CarouselProgressTrackerContent;

type SpotlightCommon = {
  id: string;
  postDate: number;
  newUntil: number | null;
  size: "small" | "medium" | "large";
  background: Image | undefined;
  video: string | null;
  targeting: TargetingWithoutExclude | null;
  relatedMedia: RelatedMediaItem[] | null;
};

export type SpotlightBasic = SpotlightCommon & {
  type: "spotlightBasic";
  title: string;
  ctas: Button[];
};

export type SpotlightPoll = SpotlightCommon & {
  type: "spotlightPoll";
  pollTitle: string;
  options: CarouselPollOption[];
};

export type SpotlightProgressTracker = SpotlightCommon & {
  type: "spotlightProgressTracker";
  tracker: ProgressTracker;
  milestones: ProgressMilestone[];
  ctas: Button[];
  title: string;
};

export type SpotlightLiveEntry = SpotlightCommon & {
  type: "spotlightLiveEntry";
  title?: string;
  subtitle?: string;
  livestreamEndTime?: string;
  livestreamStartTime?: string;
  eventPromotionName?: string;
  carouselImage?: Image;
  eventPromotionLogo?: Image;
  watchLiveButton?: Button;
  readMoreButton?: Button;
  subscribeButton?: Button;
};

export type SpotlightContent =
  | SpotlightBasic
  | SpotlightPoll
  | SpotlightProgressTracker
  | SpotlightLiveEntry;

type HeroCommon = {
  id: string;
  background: Image | undefined;
  ctas: Button[];
  targeting: TargetingWithoutExclude | null;
  relatedMedia: RelatedMediaItem[] | null;
};

export type HeroBasic = HeroCommon & {
  type: "heroBasic";
  ctas: Button[];
};

export type HeroProgressTracker = HeroCommon & {
  type: "heroProgressTracker";
  tracker: ProgressTracker;
  milestones: ProgressMilestone[];
  ctas: Button[];
  title: string;
};

export type HeroContent = HeroBasic | HeroProgressTracker;

export type ArticleHeroProgressTracker = {
  type: "articleHeroProgressTracker";
  tracker: ProgressTracker;
  milestones: ProgressMilestone[];
  ctas: Button[];
  title: string;
  background?: Image;
  relatedMedia: RelatedMediaItem[] | null;
};

export type EsportsSpecficAttributes = Pick<NewsContent, "section">;

export type NewsContent =
  | ContentJsonNewsEntry
  | ContentJsonVideoEntry
  | ContentJsonImageNewsEntry
  | ContentJsonEsportRewatchEntry
  | ContentJsonExternalLinkEntry;

export type FullscreenContent = FullscreenContentCarousel;

export type EsportFullscreenContent = FullscreenContent;

export type EsportsNewsContent = NewsContent & EsportsSpecficAttributes;

export type LiveEsportCarouselContent = {
  id: string;
  type: "liveEntry";

  title?: string;
  subtitle?: string;
  livestreamEndTime?: string;
  livestreamStartTime?: string;
  eventPromotionName?: string;
  carouselImage?: Image;
  eventPromotionLogo?: Image;

  watchLiveButton?: Button;
  readMoreButton?: Button;
  subscribeButton?: Button;

  targeting: TargetingWithoutExclude | null;
  relatedMedia: RelatedMediaItem[] | null;
};

export type EsportCarouselEventContent = {
  id: string;
  type: "eventEntry";
  title?: string;
  subtitle?: string;
  carouselImage?: Image;
  ctaButtons?: Button[];
  targeting: TargetingWithoutExclude | null;
  relatedMedia: RelatedMediaItem[] | null;
};

export type EsportCarouselContent =
  | LiveEsportCarouselContent
  | EsportCarouselEventContent;

export type SocialLink = {
  linkUrl?: string;
  socialLinkImage: Image;
};

export type NewsContentJson = {
  events: CarouselContent[];
  articles: NewsContent[];
  fullscreenEntries: FullscreenContent[];
  globals: Record<string, string>;
  socialLinks: SocialLink[];
  spotlights: SpotlightContent[];
  heroes: HeroContent[];
};

export type EsportContentJson = {
  fullscreenEntries: EsportFullscreenContent[];
  articles: EsportsNewsContent[];
  carouselItems: EsportCarouselContent[];
  globals: Record<string, string>;
  socialLinks: SocialLink[];
  spotlights: SpotlightContent[];
};

export type CommunityContentJsonEntry = {
  id: number | string;
  postDate: number;
  expiryDate: null;
  lang: string;
  section: "communityEntries";
  type: "communityEntry" | "liveStreamCommunityEntry";
  author: string;
  title: string;
  creator: {
    name: string;
    icon?: string;
  };
  videoUrl: string;
  embedVideo: boolean;
  thumbnail: Image | undefined;
  newUntil?: number;
  submissionInfo: string;
  livestreamEndDate?: number | null;
  livestreamStartDate?: number | null;

  embed?: {
    url: string;
    provider: string;
    id: string;
  };
};

export type CommunityContentJson = {
  articles: CommunityContentJsonEntry[];
  globals: Record<string, string>;
};

export type BlogImage = {
  title: string;
  url: string;
  width: number;
  height: number;
};

export type BlogBodyVideo = {
  __typename: "VideoBlock";
  title: string;
  videoUrl: string;
  videoThumbnail: BlogImage | undefined;
};

export type BlogBodyImage = {
  __typename: "ImageBlock";
  image: BlogImage | undefined;
};

export type BlogBodyText = {
  __typename: "TextBlock";
  title: string;
  text: {
    json?: unknown;
  };
};

export type BlogBodyFeature = {
  __typename: "FeatureBlock";
  title: string;
  featureThumbnail: BlogImage | undefined;
  featureText: unknown;
};

export type BlogBodyEntry =
  | BlogBodyVideo
  | BlogBodyImage
  | BlogBodyText
  | BlogBodyFeature;

export type BlogContentMeta = {
  openGraph: {
    title: string;
    siteName: string;
    description: string;
    image: string | undefined;
  };

  twitter: {
    card: string;
    site: string;
    creator: string;
    title: string;
    description: string;
    image: string | undefined;
  };
};

export type BlogSlug = {
  category: string;
  title: string;
};

export type BlogArticleContent = {
  id: string;
  slug: BlogSlug;

  articleMeta: BlogContentMeta;

  category: {
    name: string;
    color: string;
  };

  publishedAt: string;
  showInNewsFeed: boolean;
  newUntil: string | undefined;
  title: string;
  isSmallNews: boolean;

  heroImage: BlogImage | undefined;
  largeThumbnail: BlogImage | undefined;
  bodyCollection: BlogBodyEntry[];
};

export type BlogFullContent = {
  globals: Record<string, string>;
  blogMeta: BlogContentMeta;
  language: Locale;
  languageNameTranslations: {
    englishTranslation: string;
    nativeTranslation: string;
  };
  blogs: BlogArticleContent[];
};

export type BlogContentJson = BlogFullContent[];

export type ContentJson =
  | NewsContentJson
  | CommunityContentJson
  | EsportContentJson;

export interface PageKindContentMap {
  news: NewsContentJson;
  community: CommunityContentJson;
  esport: EsportContentJson;
}

export type ContentJsonFor<K extends Exclude<PageKind, "blog">> =
  PageKindContentMap[K];

export const KnownContentfulEnvironment = z.enum(["master", "staging", "dev"]);

export type KnownContentfulEnvironment = z.infer<
  typeof KnownContentfulEnvironment
>;

export const PageKind = z.enum(["news", "community", "esport", "blog"]);

export type PageKind = z.infer<typeof PageKind>;

export const ArticleVisibility = z.enum([
  "Inbox only",
  "Inbox and website",
  "Website only",
]);

export type ArticleVisibility = z.infer<typeof ArticleVisibility>;

export const LocaleTag = z.custom<`locale-${string}`>((data) => {
  const asString = z.string().parse(data);

  const [prefix, ...rest] = asString.split("-");
  const localeSuffix = Locale.parse(rest.join("-"));

  return prefix === "locale" && Locale.parse(localeSuffix);
});

export type LocaleTag = z.infer<typeof LocaleTag>;

export const CountdownTimerKind = z.enum([
  "HIDE_CTA",
  "SHOW_CTA",
  "HIDE_ENTRY",
  "DO_NOTHING",
]);

export type CountdownTimerKind = z.infer<typeof CountdownTimerKind>;

export const CountdownTimerKindLabels: Record<CountdownTimerKind, string> = {
  SHOW_CTA:
    "Show the CTA buttons (buttons will be hidden before the timer ends)",
  HIDE_CTA: "Hide the CTA buttons (buttons are shown until the timer ends)",
  HIDE_ENTRY: "Hide the entry",
  DO_NOTHING: "Do nothing (just hide the timer)",
};

export const ShowInNewsFeedKind = z.enum([
  "SHOW_IN_NEWS_FEED",
  "HIDE_IN_NEWS_FEED",
]);

export type ShowInNewsFeedKind = z.infer<typeof ShowInNewsFeedKind>;

export const ShowInNewsFeedKindLabels: Record<ShowInNewsFeedKind, string> = {
  SHOW_IN_NEWS_FEED: "Yes",
  HIDE_IN_NEWS_FEED: "No (Hides entry from news list)",
};

const CreatorUploadCommonFields = z.object({
  creatorId: z.string(),
  creatorName: z.string(),
  locale: Locale,
  title: z.string(),
  url: z.string(),
  category: z.string().nullish(),
  videoThumbnailId: z.string(),
  creatorThumbnailId: z.string().nullish(),
});

const CreatorUploadVideoFields = CreatorUploadCommonFields.extend({
  uploadType: z.literal("video"),
});

const CreatorUploadLiveFields = CreatorUploadCommonFields.extend({
  uploadType: z.literal("livestream"),
  livestreamDelay: z.number().or(z.string()),
  livestreamLength: z.number().or(z.string()),
  livestreamActiveMaxCount: z.number().or(z.string()),
});

export const CreatorUploadFields = z.discriminatedUnion("uploadType", [
  CreatorUploadVideoFields,
  CreatorUploadLiveFields,
]);

export type CreatorUploadFields = z.infer<typeof CreatorUploadFields>;

export type Parseable<T> = {
  parse: (input: unknown) => T;
};
